import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getFlightRules } from 'services/dynamic';
import { FlightRulesDisplay } from 'ts/dynamic/interfaces/DynamicResponse';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import MaximizeModal from 'components/shared/MaximizeModal';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import useDraggableModal from 'hooks/useDraggableModal';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { b2b } from 'constants/generic';
import Tabs from 'components/ui/Tabs/Tabs';

interface RulesModalProps {
  onClose: () => void;
  flightId: string;
  tokenKey: string;
}

const RulesModal: FC<RulesModalProps> = ({ onClose, flightId, ...props }) => {
  const [t] = useTranslation();
  const [urlParams] = useSearchParams();
  const [rules, setRules] = useState<FlightRulesDisplay[]>([]);
  const [choosenRuleIndex, setChoosenRuleIndex] = useState(0);
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);

  useEffect(() => {
    if (b2b && pathname !== '/dynamic/book') {
      return;
    }
    (async () => {
      const newRules = await getFlightRules({
        id: flightId,
        searchToken: urlParams.get(props.tokenKey) || '',
      });

      if (newRules) {
        setRules(newRules);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      className="modal-overlay modal-overlay-top modal-maximizable"
      ref={modalOverlayRef}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={'modal modal-sm active'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5 className="title">{t('passenger.rule_display')}</h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content text-sm flight-rules pt-10">
          {!!rules.length && (
            <Fragment>
              <Tabs
                label={t('passenger.rule_display')}
                value={String(choosenRuleIndex)}
                onValueChange={(v) => setChoosenRuleIndex(Number(v))}
                items={rules.map((rule, index) => ({
                  value: String(index),
                  label: t('passenger.rule_i', {
                    i: index + 1,
                    airlineCode: rule.airline.code,
                    fareBase: rule.fareBase,
                    arrivalCode: rule.arrivalCode,
                    departureCode: rule.departureCode,
                  }),
                }))}
              />
              <div className="wrapper">
                <div className="penalty">PE.PENALTIES</div>
                <div
                  className="content"
                  dangerouslySetInnerHTML={{
                    __html:
                      rules[choosenRuleIndex].rules
                        .find((rule) => rule.code === 'PE.PENALTIES')
                        ?.desc.replaceAll('\n', '<br>') || '',
                  }}
                ></div>
              </div>
            </Fragment>
          )}
          {!rules.length && (
            <Fragment>
              <Tabs
                label={t('passenger.rule_display')}
                items={[...Array(2)].map((_, index) => ({
                  value: String(index),
                  label: <Skeleton width={220} height={30} />,
                }))}
              />
              <div className="wrapper">
                <div className="penalty" style={{ marginRight: 10 }}>
                  <Skeleton height={22} width={120} />
                </div>
                <div className="content">
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                  <Skeleton height={22} width={220} className="mb-5" />
                  <Skeleton height={22} width={230} className="mb-5" />
                </div>
              </div>
            </Fragment>
          )}
        </div>
        <div className="modal-footer">
          <Button title={t('close')} className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(RulesModal, 'RulesModal');
